import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const PipelineTab = () => {
  const { profile } = useAuth();
  const [pipelineStages, setPipelineStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStage, setNewStage] = useState({ name: '', probability: 50, color: '#8B5CF6' });
  const [isAddStageModalOpen, setIsAddStageModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const colorOptions = [
    { value: '#94A3B8', label: 'Gray' },
    { value: '#3B82F6', label: 'Blue' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#10B981', label: 'Green' },
    { value: '#F59E0B', label: 'Yellow' },
    { value: '#EF4444', label: 'Red' },
    { value: '#EC4899', label: 'Pink' }
  ];

  const fetchStages = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('pipeline_stages')?.select('*')?.eq('org_id', profile?.org_id)?.order('stage_order');
      if (error) throw error;
      setPipelineStages(data || []);
    } catch (err) {
      setErrorMsg(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => { fetchStages(); }, [fetchStages]);

  const handleCreateStage = async () => {
    if (!newStage?.name?.trim()) return;
    setIsSaving(true);
    setErrorMsg('');
    try {
      const maxOrder = pipelineStages?.reduce((max, s) => Math.max(max, s?.stage_order || 0), 0);
      const { error } = await supabase?.from('pipeline_stages')?.insert({
        org_id: profile?.org_id,
        name: newStage?.name,
        probability: Number(newStage?.probability),
        color: newStage?.color,
        stage_order: maxOrder + 1
      });
      if (error) throw error;
      setNewStage({ name: '', probability: 50, color: '#8B5CF6' });
      setIsAddStageModalOpen(false);
      setSuccessMsg('Stage created successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchStages();
    } catch (err) {
      setErrorMsg(err?.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm('Delete this pipeline stage? Deals in this stage will lose their stage assignment.')) return;
    try {
      await supabase?.from('pipeline_stages')?.delete()?.eq('id', stageId);
      fetchStages();
    } catch (err) {
      setErrorMsg(err?.message);
    }
  };

  const handleUpdateProbability = async (stageId, probability) => {
    try {
      await supabase?.from('pipeline_stages')?.update({ probability: Number(probability) })?.eq('id', stageId);
      fetchStages();
    } catch (err) {
      setErrorMsg(err?.message);
    }
  };

  return (
    <div className="space-y-8">
      {successMsg && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{successMsg}</div>}
      {errorMsg && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errorMsg}</div>}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Icon name="GitBranch" size={24} className="text-primary" />
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">Pipeline Stages</h3>
              <p className="text-sm text-muted-foreground">Configure your sales pipeline stages and probabilities</p>
            </div>
          </div>
          <Button variant="default" onClick={() => setIsAddStageModalOpen(true)} iconName="Plus" iconPosition="left">
            Add Stage
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4]?.map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {pipelineStages?.map((stage) => (
              <div key={stage?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: stage?.color }} />
                  <div>
                    <p className="font-medium text-card-foreground">{stage?.name}</p>
                    <p className="text-sm text-muted-foreground">Order: {stage?.stage_order}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-muted-foreground">Probability:</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={stage?.probability}
                      onChange={(e) => handleUpdateProbability(stage?.id, e?.target?.value)}
                      className="w-16 px-2 py-1 border border-border rounded text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  <button
                    onClick={() => handleDeleteStage(stage?.id)}
                    className="text-error hover:text-error/80 transition-smooth"
                    aria-label="Delete stage"
                  >
                    <Icon name="Trash2" size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Add Stage Modal */}
      {isAddStageModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Add Pipeline Stage</h2>
              <button onClick={() => setIsAddStageModalOpen(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <Input label="Stage Name" type="text" value={newStage?.name}
                onChange={(e) => setNewStage(p => ({...p, name: e?.target?.value}))} required />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Probability (%)</label>
                <input type="number" min="0" max="100" value={newStage?.probability}
                  onChange={(e) => setNewStage(p => ({...p, probability: e?.target?.value}))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" />
              </div>
              <Select label="Color" options={colorOptions} value={newStage?.color}
                onChange={(value) => setNewStage(p => ({...p, color: value}))} />
            </div>
            <div className="flex space-x-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddStageModalOpen(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handleCreateStage} loading={isSaving}
                disabled={isSaving || !newStage?.name?.trim()}>
                {isSaving ? 'Creating...' : 'Create Stage'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineTab;